import { format } from 'date-fns'
import { Modal } from 'components/UI/Modal/Modal'
import { DataTable } from '../../components/DataTable'
import { DataTableContainer } from '../../components/DataTable/UI'
import { ColumnDef } from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import apiService from '../../services/api'
import { RebateClaimDetail, RebateOfferProduct } from './tradespend_api'
import { ActionButtons } from '../../components/Buttons/ActionButtons'
import { BiReceipt } from 'react-icons/bi'
import { ClaimInvoicesView } from './ClaimInvoicesView'

interface OfferUserDetailsModalProps {
  open: boolean
  onClose: () => void
  associationId: number
}

export function OfferUserDetailsModal({
  open,
  onClose,
  associationId,
}: OfferUserDetailsModalProps) {
  const api = apiService()
  const { data: userDetails, isFetching } =
    api.useGetOfferUserDetails(associationId)

  const [activeView, setActiveView] = useState<{
    type: 'claims' | 'invoices'
    claim?: RebateClaimDetail
  }>({ type: 'claims' })

  const { productMap, variantMap } = useMemo(() => {
    if (!userDetails || userDetails.is_subscription_only)
      return { productMap: {}, variantMap: {} }

    const productMap: Record<string, RebateOfferProduct> = {}
    const variantMap: Record<
      string,
      RebateOfferProduct['product_variants'][0]
    > = {}

    userDetails.terms.products.forEach((product: RebateOfferProduct) => {
      productMap[product.product_id.toString()] = product
      product.product_variants.forEach(
        (variant: RebateOfferProduct['product_variants'][0]) => {
          variantMap[variant.variant_id.toString()] = variant
        }
      )
    })

    return { productMap, variantMap }
  }, [userDetails])

  const claimColumns = useMemo<ColumnDef<RebateClaimDetail, any>[]>(
    () => [
      {
        accessorKey: 'created',
        header: 'Created',
        size: 150,
        cell: (info) => format(new Date(info.getValue()), 'MMM d, yyyy'),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 150,
      },
      {
        accessorKey: 'total_units',
        header: 'Total Units',
        size: 120,
        cell: (info) => {
          const units = info.getValue()
          return units !== null
            ? units.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : '-'
        },
      },
      {
        accessorKey: 'payout_amount',
        header: 'Payout Amount',
        size: 120,
        cell: (info) => {
          const amount = info.getValue()
          return amount !== null
            ? `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : '-'
        },
      },
      {
        id: 'invoices',
        header: 'View Invoices',
        size: 100,
        meta: {
          isLastColumn: true,
          rightAlign: true,
        },
        cell: (info) => {
          const claim = info.row.original
          return (
            <ActionButtons
              className="justify-end"
              onSee={() => setActiveView({ type: 'invoices', claim })}
              seeIcon={BiReceipt}
            />
          )
        },
      },
    ],
    []
  )

  const renderContent = () => {
    if (!userDetails) {
      return (
        <div className="flex justify-center items-center h-40">
          <span className="text-gray-500">Loading...</span>
        </div>
      )
    }

    // Handle subscription-only view
    if (userDetails.is_subscription_only) {
      return (
        <div className="space-y-4">
          <div className="bg-new-company-pistachio-light border border-new-company-pistachio-medium rounded-lg p-4 mt-6">
            <h3 className="text-lg font-semibold text-new-company-green-dark mb-2">
              Subscription Only
            </h3>
            <p className="text-new-company-green-active">
              This user has subscribed to the offer but has not enrolled yet.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">
                Subscription Information
              </h3>
              <p>
                <span className="font-bold">Email:</span>{' '}
                {userDetails.subscription_email}
              </p>
              <p>
                <span className="font-bold">Subscribed On:</span>{' '}
                {userDetails.subscribed_at
                  ? format(new Date(userDetails.subscribed_at), 'MMM d, yyyy')
                  : format(new Date(userDetails.created), 'MMM d, yyyy')}
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">Offer Information</h3>
              <p>
                <span className="font-bold">Offer:</span>{' '}
                {userDetails.offer_name}
              </p>
              <p>
                <span className="font-bold">Code:</span>{' '}
                {userDetails.offer_code}
              </p>
            </div>
          </div>
        </div>
      )
    }

    // Handle enrollment view with invoices
    if (activeView.type === 'invoices' && activeView.claim) {
      return (
        <ClaimInvoicesView
          claim={activeView.claim}
          onBack={() => setActiveView({ type: 'claims' })}
          productMap={productMap}
          variantMap={variantMap}
        />
      )
    }

    // Handle full enrollment details
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <h3 className="text-lg font-semibold mb-2">
              Enrollment Information
            </h3>
            <p>
              <span className="font-bold">User:</span>{' '}
              {userDetails.tradespend_user_name}
            </p>
            <p>
              <span className="font-bold">Company:</span>{' '}
              {userDetails.company_name}
            </p>
            <p>
              <span className="font-bold">Enrolled On:</span>{' '}
              {format(new Date(userDetails.created), 'MMM d, yyyy')}
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-2">Claims Summary</h3>
            <p>
              <span className="font-bold">Total Claims:</span>{' '}
              {userDetails.total_claims}
            </p>
            <p>
              <span className="font-bold">Pending Validation:</span>{' '}
              {userDetails.pending_validation_claims}
            </p>
            <p>
              <span className="font-bold">Invalid Claims:</span>{' '}
              {userDetails.invalid_claims}
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-2">Payment Status</h3>
            <p>
              <span className="font-bold">Paid Claims:</span>{' '}
              {userDetails.paid_claims}
            </p>
            <p>
              <span className="font-bold">Unpaid Valid Claims:</span>{' '}
              {userDetails.unpaid_valid_claims}
            </p>
          </div>
        </div>

        <hr className="h-px my-4 bg-gray-300 border-0 dark:bg-gray-700" />

        <div>
          <h3 className="text-lg font-semibold mb-2">Claims History</h3>
          <DataTableContainer>
            <DataTable
              loading={isFetching}
              height={400}
              data={userDetails.claims}
              columns={claimColumns}
              tableKey="enrollment-claims"
              expandColAccessor="expanded-row"
            />
          </DataTableContainer>
        </div>
      </div>
    )
  }

  const getModalTitle = () => {
    if (!userDetails) return 'User Details'

    if (userDetails.is_subscription_only) {
      return (
        <p className="flex items-center gap-2">
          Subscription Details
          <span className="font-light">{userDetails.subscription_email}</span>
        </p>
      )
    }

    return (
      <p className="flex items-center gap-2">
        Enrollment Details
        {userDetails.company_name && (
          <span className="font-light">{userDetails.company_name}</span>
        )}
      </p>
    )
  }

  return (
    <Modal
      size="2xl"
      open={open}
      onOpenChange={onClose}
      title={getModalTitle()}
      childrenContainerClassName="max-h-[70vh] overflow-y-auto max-w-full w-full"
      dialogFooterClassName="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2"
    >
      {renderContent()}
    </Modal>
  )
}
